import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  GraduationCap, CheckCircle2, Circle, ArrowLeft, Award, RotateCcw, Loader2, PartyPopper,
} from "lucide-react";
import { AppHeader } from "../../components/AppHeader";
import { Button } from "../../components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { TRAINING_MODULES } from "../../lib/trainingConfig";
import api from "../../lib/api";

export default function TrainingPortal() {
  const [app, setApp] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/application/me");
      if (!data.training_unlocked) { navigate("/portal", { replace: true }); return; }
      setApp(data);
      if (!data.training_started_at) { try { await api.post("/application/me/start-training"); } catch {} }
      return data;
    } catch { toast.error("Could not load training."); }
  };
  useEffect(() => { load(); }, []);

  const completed = useMemo(() => new Set(app?.training_modules_completed || []), [app]);
  const allModulesDone = TRAINING_MODULES.every((m) => completed.has(m.id));
  const passed = app?.training_quiz?.passed;

  useEffect(() => {
    if (allModulesDone && !passed && !quiz) {
      api.get("/application/me/training/quiz").then(({ data }) => setQuiz(data)).catch(() => {});
    }
  }, [allModulesDone, passed, quiz]);

  const markComplete = async (id) => {
    try {
      const { data } = await api.post(`/application/me/training/module/${id}`);
      setApp(data);
      toast.success("Lesson completed");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  const submitQuiz = async () => {
    if (Object.keys(answers).length < (quiz?.questions.length || 0)) {
      return toast.error("Please answer every question.");
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/application/me/training/quiz", { answers });
      setResult(data);
      setApp(data.application);
      if (data.passed) toast.success(`You passed with ${data.score}%!`);
      else toast.error(`Score ${data.score}% — ${quiz.pass_pct}% needed. Please review and try again.`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit quiz"); }
    finally { setSubmitting(false); }
  };

  const retake = () => { setResult(null); setAnswers({}); };

  if (!app) return <div className="min-h-screen bg-[#FAF9F6]" />;

  const doneCount = completed.size;
  const pct = Math.round((doneCount / TRAINING_MODULES.length) * 100);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <button onClick={() => navigate("/portal")} className="mb-4 inline-flex items-center gap-2 text-sm text-[#515E58] hover:text-[#1F2924]" data-testid="back-to-portal">
          <ArrowLeft className="h-4 w-4" /> Back to portal
        </button>

        <div className="rounded-2xl bg-[#2C5545] p-8 text-white">
          <div className="flex items-center gap-3">
            <GraduationCap className="h-7 w-7" />
            <h1 className="font-heading text-2xl font-bold sm:text-3xl">GIC&E Contractor Training</h1>
          </div>
          <p className="mt-2 max-w-2xl text-white/85">
            Complete each lesson below, then pass the competency quiz to finish training. You need
            {" "}{quiz?.pass_pct || 80}% to pass.
          </p>
          <div className="mt-5">
            <div className="mb-1 flex justify-between text-sm text-white/80">
              <span>{doneCount} of {TRAINING_MODULES.length} lessons complete</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-white/20">
              <div className="h-full rounded-full bg-[#D56A47] transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        </div>

        {passed ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="mt-6 rounded-2xl border border-[#3A7D5E]/30 bg-[#EEF5F0] p-10 text-center" data-testid="training-complete">
            <PartyPopper className="mx-auto h-12 w-12 text-[#3A7D5E]" />
            <h2 className="mt-4 font-heading text-2xl font-bold text-[#1F2924]">Training Completed!</h2>
            <p className="mt-2 text-[#515E58]">
              You passed the competency quiz with {app.training_quiz?.score}%. You are now ready for the final
              interview / onboarding phase. Our team will be in touch with next steps.
            </p>
            <Button onClick={() => navigate("/portal")} className="mt-6 rounded-lg bg-[#2C5545] px-6 hover:bg-[#224336]">Return to Portal</Button>
          </motion.div>
        ) : (
          <>
            {/* Lessons */}
            <div className="mt-6">
              <h2 className="mb-3 font-heading text-lg font-semibold text-[#1F2924]">Lessons</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {TRAINING_MODULES.map((m, i) => {
                  const done = completed.has(m.id);
                  return (
                    <AccordionItem key={m.id} value={m.id}
                      className="overflow-hidden rounded-xl border border-[#E5E2DC] bg-white px-0" data-testid={`module-${m.id}`}>
                      <AccordionTrigger className="px-5 hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          {done ? <CheckCircle2 className="h-5 w-5 shrink-0 text-[#3A7D5E]" /> : <Circle className="h-5 w-5 shrink-0 text-[#C7C2B8]" />}
                          <div>
                            <div className="font-heading text-base font-semibold text-[#1F2924]">{i + 1}. {m.title}</div>
                            <div className="text-xs text-[#515E58]">{m.minutes} min read{done ? " · Completed" : ""}</div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5">
                        <p className="text-[15px] italic text-[#515E58]">{m.intro}</p>
                        <div className="mt-4 space-y-5">
                          {m.sections.map((s, j) => (
                            <div key={j}>
                              <h4 className="font-heading font-semibold text-[#2C5545]">{s.heading}</h4>
                              {s.paragraphs?.map((p, k) => <p key={k} className="mt-2 text-[15px] leading-relaxed text-[#3B463F]">{p}</p>)}
                              {s.bullets && (
                                <ul className="mt-2 list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-[#3B463F]">
                                  {s.bullets.map((b, k) => <li key={k}>{b}</li>)}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                        <Button onClick={() => markComplete(m.id)} disabled={done} data-testid={`complete-module-${m.id}`}
                          className="mt-5 rounded-lg bg-[#2C5545] px-6 hover:bg-[#224336] disabled:opacity-60">
                          {done ? "Lesson Completed" : "Mark Lesson Complete"}
                        </Button>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </div>

            {/* Quiz */}
            <div className="mt-8" data-testid="quiz-section">
              <div className="flex items-center gap-2">
                <Award className="h-5 w-5 text-[#D56A47]" />
                <h2 className="font-heading text-lg font-semibold text-[#1F2924]">Competency Quiz</h2>
              </div>
              {!allModulesDone ? (
                <div className="mt-3 rounded-xl border border-dashed border-[#E5E2DC] bg-[#F4F2EC] p-6 text-center text-[#515E58]" data-testid="quiz-locked">
                  Complete all {TRAINING_MODULES.length} lessons above to unlock the quiz.
                </div>
              ) : result && !result.passed ? (
                <div className="mt-3 rounded-xl border border-[#C44F4F]/30 bg-[#FBEDED] p-6 text-center" data-testid="quiz-failed">
                  <p className="font-heading text-lg font-semibold text-[#C44F4F]">Score: {result.score}%</p>
                  <p className="mt-1 text-sm text-[#8A3A3A]">You need {quiz.pass_pct}% to pass. Review the lessons and try again.</p>
                  <Button onClick={retake} data-testid="retake-quiz-btn" className="mt-4 gap-2 rounded-lg bg-[#2C5545] px-6 hover:bg-[#224336]">
                    <RotateCcw className="h-4 w-4" /> Retake Quiz
                  </Button>
                </div>
              ) : quiz ? (
                <div className="mt-3 space-y-4">
                  {quiz.questions.map((q, i) => (
                    <div key={q.id} className="rounded-xl border border-[#E5E2DC] bg-white p-5" data-testid={`quiz-q-${q.id}`}>
                      <p className="font-medium text-[#1F2924]">{i + 1}. {q.question}</p>
                      <RadioGroup value={answers[q.id]?.toString() ?? ""} onValueChange={(v) => setAnswers((a) => ({ ...a, [q.id]: Number(v) }))}
                        className="mt-3 space-y-2">
                        {q.options.map((opt, idx) => (
                          <label key={idx} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 text-sm transition-colors ${answers[q.id] === idx ? "border-[#2C5545] bg-[#EAF0ED]" : "border-[#E5E2DC] hover:bg-[#F4F2EC]"}`}>
                            <RadioGroupItem value={idx.toString()} data-testid={`quiz-${q.id}-opt-${idx}`} />
                            <span className="text-[#1F2924]">{opt}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  ))}
                  <Button onClick={submitQuiz} disabled={submitting} data-testid="submit-quiz-btn"
                    className="w-full gap-2 rounded-lg bg-[#2C5545] py-6 text-base font-semibold hover:bg-[#224336]">
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Award className="h-5 w-5" />} Submit Quiz
                  </Button>
                </div>
              ) : (
                <div className="mt-3 text-sm text-[#515E58]">Loading quiz…</div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
